import { useEffect, useMemo, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronDown,
  ChevronRight,
  Search,
  Download,
  X,
  Star,
  Ban,
} from 'lucide-react'

import merchantApi from '../../services/merchantApi'
import './CustomersPage.scss'
import Header from '../../components/Header/Header'
import { Icon } from '@iconify/react/dist/iconify.js'
import AppTable from '../../components/AppTable/AppTable'
import { useNavigate } from 'react-router-dom'
import FilterSideModal from '../../components/FilterSideModal/FilterSideModal'
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown'
import { Avatar } from '@mui/material'
import AppPagination from '../../components/AppPagination/AppPagination'

const moduleCode = 'customer'

const ADJUST_REASONS = [
  'Correction - Duplicate points',
  'Goodwill gesture',
  'Promotional reward',
  'Event bonus',
  'Other',
]

const unwrapList = (response) => {
  const payload = response?.data ?? response
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.customers)) return payload.customers
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.records)) return payload.records
  if (Array.isArray(payload?.histories?.data)) return payload.histories.data
  return []
}

const unwrapItem = (response) => {
  const payload = response?.data ?? response
  return payload?.customer ?? payload?.data ?? payload ?? null
}

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return 'RM0.00'
  if (typeof value === 'string' && value.trim().startsWith('RM')) return value
  const amount = Number(value)
  return Number.isFinite(amount) ? `RM${amount.toFixed(2)}` : String(value)
}

const formatDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('en-MY', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const formatDateTime = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('en-MY', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const normalizeCustomer = (raw = {}) => {
  console.log('normalizeCustomer', raw)
  const name = raw.name || raw.full_name || raw.customer_name || 'Unnamed customer'
  const statusValue = raw.status ?? raw.is_active ?? 1
  const status = String(statusValue === 1 || statusValue === true ? 'active' : statusValue === 0 || statusValue === false ? 'inactive' : statusValue === -1 ? 'suspended' : statusValue).toLowerCase()
  const points = numberValue(raw.points ?? raw.point ?? raw.points_balance ?? raw.balance ?? raw.total_points, 0)
  const orders = numberValue(raw.orders_count ?? raw.order_count ?? raw.total_orders ?? raw.orders, 0)
  const totalSpent = raw.total_spent ?? raw.totalSpent ?? raw.total_amount ?? raw.lifetime_value ?? 0
  const avgOrder = raw.avg_order_value ?? raw.average_order ?? raw.avgOrder ?? (orders > 0 ? numberValue(totalSpent) / orders : 0)
  const reason = raw.status_reason ?? null
  const actionBy = raw?.status_changed_by?.name ?? null

  return {
    id: raw.id ?? raw.customer_id,
    raw,
    name,
    email: raw.email || '-',
    phone: raw.phone || raw.phone_number || raw.mobile || raw.contact_number || '-',
    status,
    points,
    source: raw.source || raw.signup_source || raw.channel || '',
    orders,
    totalSpent: formatMoney(totalSpent),
    avgOrder: avgOrder === '-' ? '-' : formatMoney(avgOrder),
    memberSince: formatDate(raw.member_since || raw.created_at),
    signupSource: raw.signup_source || raw.source || raw.channel || '',
    created: formatDateTime(raw.created_at),
    lastLogin: formatDate(raw.last_login_at || raw.last_login || raw.updated_at),
    avatar: raw.avatar || raw.avatar_url || raw.profile_photo_url,
    reason: reason,
    actionBy: actionBy,
  }
}

const normalizeOrder = (raw = {}) => ({
  id: raw.order_number || raw.id || raw.order_id || '-',
  rawId: raw.id || raw.order_id,
  posStatus: String(raw.pos_status || raw.status || 'open').toLowerCase(),
  type: raw.order_type || raw.type || raw.fulfillment_type || raw.source || '',
  type_label: raw.order_type_label || '',
  total: formatMoney(raw.total ?? raw.grand_total ?? raw.total_amount ?? raw.amount),
  placed: formatDateTime(raw.created_at || raw.placed_at || raw.order_date),
})

const normalizePoint = (raw = {}) => {
  const amount = numberValue(raw.amount ?? raw.points ?? raw.delta ?? raw.value, 0)
  const type = raw.type || raw.transaction_type || (amount >= 0 ? 'add' : 'deduct')

  return {
    type,
    label: raw.label || raw.description || raw.reason || (type === 'deduct' ? 'Points deducted' : 'Points added'),
    date: formatDateTime(raw.created_at || raw.date || raw.transaction_date),
    delta: amount,
    balance: numberValue(raw.balance ?? raw.points_balance ?? raw.after_balance, 0),
    reason: raw.reason || raw.remark || raw.remarks || null,
  }
}

function CustomerNameTag({ customer }) {
  return (
    <div className="cdp-modal__customer-card">
      {
        customer?.avatar ? <img src={customer.avatar} alt={customer.name} />
          : <Avatar className="sr-table__mui-avatar" />
      }
      <div>
        <p className="cdp-modal__card-name">{customer.name}</p>
        <p className="cdp-modal__card-pts"><Star size={14} className="cdp-modal__star" /> {customer.points.toLocaleString()} pts · Total {customer.orders} orders</p>
      </div>
      <span className={`cdp-modal__card-status customers-status customers-status--${customer.status}`}><Icon icon="ph:circle-fill" height={5} /> {customer.status}</span>
    </div>
  )
}

function AdjustPointsModal({ customer, onClose, onAdjusted }) {
  const [adjType, setAdjType] = useState('add')
  const [quantity, setQuantity] = useState('')
  const [reason, setReason] = useState(ADJUST_REASONS[0])
  const [remarks, setRemarks] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!quantity || Number(quantity) <= 0) return

    setSubmitting(true)
    setError('')

    try {
      await merchantApi.customers.adjustPoints(customer.id, {
        type: adjType,
        amount: Number(quantity),
        reason,
        remark: remarks,
      })
      await onAdjusted?.()
      onClose()
    } catch (err) {
      setError(err?.message || 'Failed to adjust points.')
    } finally {
      setSubmitting(false)
    }
  }

  return createPortal(
    <div className="cdp-modal-overlay" onClick={onClose}>
      <div className="cdp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cdp-modal__header">
          <h3>Adjust points</h3>
          <button className="cdp-modal__close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="cdp-modal__body">
          {error && <p className="cdp-empty" style={{ color: '#b42318' }}>{error}</p>}

          <CustomerNameTag customer={customer} />

          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Adjustment type</label>
            <div className="cdp-modal__radio-row">
              <label className="cdp-modal__radio">
                <input type="radio" name="adjType" value="add" checked={adjType === 'add'} onChange={() => setAdjType('add')} />
                <span className="cdp-modal__radio-circle" />
                Add points
              </label>
              <label className="cdp-modal__radio">
                <input type="radio" name="adjType" value="deduct" checked={adjType === 'deduct'} onChange={() => setAdjType('deduct')} />
                <span className="cdp-modal__radio-circle" />
                Deduct points
              </label>
            </div>
          </div>

          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Quantity to adjust</label>
            <input
              className="cdp-modal__input"
              type="number"
              min="1"
              placeholder="e.g. 100"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>

          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Reason for adjustment</label>
            <div className="cdp-modal__select-wrap">
              <select className="cdp-modal__select" value={reason} onChange={(e) => setReason(e.target.value)}>
                {ADJUST_REASONS.map((r) => <option key={r}>{r}</option>)}
              </select>
              <Icon icon={'ph:caret-down'} className="cdp-modal__select-icon" height={16} />
            </div>
          </div>

          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Remarks <span className="cdp-modal__optional">(optional)</span></label>
            <textarea
              className="cdp-modal__textarea"
              rows={4}
              placeholder="Add any additional notes or context for this adjustment."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
        </div>

        <div className="cdp-modal__footer">
          <button className="cdp-modal__cancel" onClick={onClose}>Cancel</button>
          <button className="cdp-modal__confirm" disabled={!quantity || submitting} onClick={handleSubmit}>
            {submitting ? 'Saving...' : 'Confirm adjustment'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function SuspendModal({ customer, onClose, onConfirm }) {
  const [reason, setReason] = useState('')

  return createPortal(
    <div className="cdp-modal-overlay" onClick={onClose}>
      <div className="cdp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cdp-modal__header">
          <h3>Suspend customer account</h3>
          <button className="cdp-modal__close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="cdp-modal__body">
          <p className="cdp-modal__suspend-desc">
            This action will temporarily disable the customer's account for this outlet only
          </p>

          <CustomerNameTag customer={customer} />

          <ul className="cdp-modal__consequences">
            <li>The customer cannot access merchant customer features.</li>
            <li>The account can be reactivated later from the backend.</li>
            <li>The action will be recorded for audit purposes.</li>
          </ul>

          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Reason for suspension</label>
            <textarea
              className="cdp-modal__textarea"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="cdp-modal__footer">
          <button className="cdp-modal__cancel" onClick={onClose}>Cancel</button>
          <button className="cdp-modal__confirm cdp-modal__confirm--danger" onClick={() => { onConfirm(reason); onClose() }}>
            <Ban size={18} /> Suspend account
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function DeactivateModal({ customer, onClose, onConfirm }) {
  const [reason, setReason] = useState('')

  return createPortal(
    <div className="cdp-modal-overlay" onClick={onClose}>
      <div className="cdp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cdp-modal__header">
          <h3>Set account to inactive</h3>
          <button className="cdp-modal__close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="cdp-modal__body">
          <p className="cdp-modal__suspend-desc">
            This action will deactivate the customer's account for this outlet only. No fault is implied — this is an administrative action.
          </p>

          <CustomerNameTag customer={customer} />

          <ul className="cdp-modal__consequences">
            <li>Customer will not be able to place new orders.</li>
            <li>Existing points and order history are fully preserved.</li>
            <li>Account can be reactivated by staff at any time.</li>
          </ul>

          <div className="cdp-modal__field">
            <label className="cdp-modal__label">Reason for deactivation</label>
            <textarea
              className="cdp-modal__textarea"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="cdp-modal__footer">
          <button className="cdp-modal__cancel" onClick={onClose}>Cancel</button>
          <button className="cdp-modal__confirm cdp-modal__confirm--danger" onClick={() => { onConfirm(reason); onClose() }}>
            <Icon icon="ph:moon" height={18} /> Set to inactive
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function RestoreModal({ customer, onClose, onConfirm }) {

  return createPortal(
    <div className="cdp-modal-overlay" onClick={onClose}>
      <div className="cdp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cdp-modal__header">
          <h3>Restore account access</h3>
          <button className="cdp-modal__close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="cdp-modal__body">
          <p className="cdp-modal__suspend-desc">
            This action will restore the customer's full access to this outlet.
          </p>

          <CustomerNameTag customer={customer} />

          <ul className="cdp-modal__consequences">
            <li>Customer can place orders again immediately.</li>
            <li>Existing points and order history are unchanged.</li>
            <li>Previously applied voucher restrictions are cleared.</li>
          </ul>
        </div>

        <div className="cdp-modal__footer">
          <button className="cdp-modal__cancel" onClick={onClose}>Cancel</button>
          <button className="cdp-modal__confirm cdp-modal__confirm--primary" onClick={() => { onConfirm(); onClose() }}>
            <Icon icon="ph:check-circle" height={18} /> Restore access
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function ReactivateModal({ customer, onClose, onConfirm }) {

  return createPortal(
    <div className="cdp-modal-overlay" onClick={onClose}>
      <div className="cdp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cdp-modal__header">
          <h3>Reactivate customer account</h3>
          <button className="cdp-modal__close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="cdp-modal__body">
          <p className="cdp-modal__suspend-desc">
            This action will reactivate the customer's account for this outlet only.
          </p>

          <CustomerNameTag customer={customer} />

          <ul className="cdp-modal__consequences">
            <li>Customer can place orders and earn points again.</li>
            <li>All order history and loyalty points are preserved.</li>
            <li>Account status will return to Active immediately.</li>
          </ul>
        </div>

        <div className="cdp-modal__footer">
          <button className="cdp-modal__cancel" onClick={onClose}>Cancel</button>
          <button className="cdp-modal__confirm cdp-modal__confirm--primary" onClick={() => { onConfirm(); onClose() }}>
            <Icon icon="ph:arrows-counter-clockwise" height={18} /> Reactivate account
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function OverviewTab({ customer, onAdjustPoints, onSuspend, onDeactivate, onRestore, onReactivate, editable }) {
  return (
    <div className="cdp-tab-content">
      <p className="cdp-section-label">BASIC INFO</p>
      <div className="cdp-info-list">
        <div className="cdp-info-row"><span>Name:</span><strong>{customer.name}</strong></div>
        <div className="cdp-info-row"><span>Phone number:</span><strong>{customer.phone}</strong></div>
        <div className="cdp-info-row"><span>Email address:</span><strong>{customer.email || '-'}</strong></div>
        {/* <div className="cdp-info-row"><span>Signup source:</span><strong>{customer.signupSource}</strong></div> */}
        <div className="cdp-info-row"><span>Created:</span><strong>{customer.created}</strong></div>
      </div>

      <p className="cdp-section-label" style={{ marginTop: 28 }}>STATS</p>
      <div className="cdp-stats-grid">
        <div className="cdp-stat"><span>ORDERS</span><h3>{customer.orders}</h3></div>
        <div className="cdp-stat"><span>TOTAL SPENT</span><h3>{customer.totalSpent}</h3></div>
        <div className="cdp-stat"><span>POINTS BALANCE</span><h3>{customer.points.toLocaleString()} pts</h3></div>
        <div className="cdp-stat"><span>AVG ORDERS</span><h3>{customer.avgOrder}</h3></div>
      </div>

      <p className="cdp-section-label" style={{ marginTop: 28 }}>ACTIONS</p>
      <div className="cdp-actions">
        {
          customer?.status === 'active' && editable &&
          <>
            <button className="cdp-action-btn" onClick={onAdjustPoints}>
              <Icon icon="ph:star" height={16} /> Adjust points manually
            </button>
            <button className="cdp-action-btn" onClick={onDeactivate}>
              <Icon icon="ph:minus-circle" height={16} /> Deactivate account
            </button>
            <button className="cdp-action-btn cdp-action-btn--danger" onClick={onSuspend}>
              <Icon icon="ph:prohibit" height={16} /> Suspend account
            </button>
          </>
        }
        {
          customer?.status === 'suspended' && editable &&
          <>
            <button className="cdp-action-btn cdp-action-btn--primary" onClick={onRestore}>
              <Icon icon="ph:check-circle" height={16} /> Restore access
            </button>
          </>
        }
        {
          customer?.status === 'inactive' && editable &&
          <>
            <button className="cdp-action-btn cdp-action-btn--primary" onClick={onReactivate}>
              <Icon icon="ph:arrows-counter-clockwise" height={16} /> Reactivate account
            </button>
          </>
        }
      </div>
    </div>
  )
}

function OrdersTab({ customer, navigate }) {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  let mounted = true

  useEffect(() => {
    loadOrders()

    return () => {
      mounted = false
    }
  }, [customer.id])

  async function loadOrders() {
    setLoading(true)
    setError('')
    try {
      const response = await merchantApi.orders.list({ customer_id: customer.id })
      setOrders(unwrapList(response).map(normalizeOrder))
    } catch (err) {
      setOrders([])
      setError(err?.message || 'Failed to load customer orders.')
    } finally {
      setLoading(false)
    }
  }

  const posStatusClass = (s) => {
    if (s === 'open' || s === 'pending' || s === 'confirmed') return { style: 'odp__badge--pos-open', label: 'Open' }
    if (s === 'closed' || s === 'completed' || s === 'ready') return { style: 'odp__badge--pos-closed', label: 'Closed' }
    if (s === 'cancelled' || s === 'canceled') return { style: 'odp__badge--pos-cancelled', label: 'Cancelled' }
    return { style: '', label: '' }
  }

  const typeClass = (type) => {
    if (type === 'dine_in') return 'cdp-order-type--dinein'
    if (type === 'takeaway') return 'cdp-order-type--pickup'
    if (type === 'kiosk') return 'cdp-order-type--kiosk'
    return ''
  }

  const columns = [
    {
      field: 'id',
      header: 'ORDER ID',
      render: (row) => (
        <div>
          <p className="cdp-orders-table__id">{row.id}</p>
          <span className={`cdp-order-type ${typeClass(row.type)}`}>• {row.type_label}</span>
        </div>
      )
    },
    {
      field: 'posStatus',
      header: 'STATUS',
      render: (row) => {
        console.log("row", row)
        return (
          <span className={`odp__badge odp__badge--pos  ${posStatusClass(row.posStatus).style}`}>
            <Icon icon="ph:circle-fill" height={5} /> {posStatusClass(row.posStatus).label}
          </span>
        )
      }
    },
    {
      field: 'total',
      header: 'TOTAL'
    },
    {
      field: 'placed',
      header: 'PLACED'
    }
  ]

  return (
    <div className="cdp-tab-content">
      {error && <p className="cdp-empty" style={{ color: '#b42318' }}>{error}</p>}
      <AppTable
        columns={columns}
        rows={orders}
        loading={loading}
        emptyText="No orders found."
        onRowClick={(row) => navigate(`/orders/${row.rawId || row.id}`)}
        noPagination={true}
      />

      <button className="cdp-view-all" onClick={() => navigate("/orders")}>View all orders</button>
    </div>
  )
}

function PointsTab({ customer, onAdjustPoints, refreshKey }) {
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [meta, setMeta] = useState(null)

  useEffect(() => {
    let mounted = true

    async function loadPoints() {
      setLoading(true)
      setError('')
      try {
        const response = await merchantApi.customers.points(customer.id, { page: currentPage })
        if (!mounted) return
        setHistory(unwrapList(response).map(normalizePoint))
        setMeta({
          total: response.data?.histories?.total ?? 0,
          from: response.data?.histories?.from ?? 0,
          to: response.data?.histories?.to ?? 0,
          last_page: response.data?.histories?.last_page ?? 1,
        })
      } catch (err) {
        if (!mounted) return
        setHistory([])
        setError(err?.message || 'Failed to load points history.')
      } finally {
        if (mounted) setLoading(false)
      }
    }

    loadPoints()

    return () => {
      mounted = false
    }
  }, [customer.id, refreshKey])

  return (
    <div className="cdp-tab-content">
      <div className="cdp-points-header">
        <div>
          <p className="cdp-section-label">POINTS BALANCE</p>
          <p className="cdp-points-balance">{customer.points.toLocaleString()} pts</p>
        </div>
        <button className="cdp-adjust-btn" onClick={onAdjustPoints}>Adjust points</button>
      </div>

      <p className="cdp-section-label" style={{ marginTop: 28 }}>POINTS HISTORY</p>
      {error && <p className="cdp-empty" style={{ color: '#b42318' }}>{error}</p>}
      <div className="cdp-points-list">
        {history.map((entry, i) => (
          <div key={`${entry.date}-${i}`} className="cdp-points-entry">
            <div className="cdp-points-entry__left">
              <p className="cdp-points-entry__label">{entry.label}</p>
              <p className="cdp-points-entry__date">{entry.date}</p>
              {entry.reason && <p className="cdp-points-entry__reason">Reason: {entry.reason}</p>}
            </div>
            <div className="cdp-points-entry__right">
              <p className={`cdp-points-entry__delta ${entry.delta > 0 ? 'cdp-points-entry__delta--plus' : 'cdp-points-entry__delta--minus'}`}>
                {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
              </p>
              <p className="cdp-points-entry__balance">Balance: {entry.balance}</p>
            </div>
          </div>
        ))}

        {loading && <p className="cdp-empty">Loading points history...</p>}
        {!loading && history.length === 0 && !error && <p className="cdp-empty">No points history.</p>}
      </div>
      <AppPagination
        currentPage={currentPage}
        pagination={meta}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </div>
  )
}

function CustomerDetailsPanel({ customer, onClose, onCustomerUpdated, editable }) {
  const [activeTab, setActiveTab] = useState('overview')
  const [showAdjust, setShowAdjust] = useState(false)
  const [showSuspend, setShowSuspend] = useState(false)
  const [showDeactivate, setShowDeactivate] = useState(false)
  const [showRestore, setShowRestore] = useState(false)
  const [showReactivate, setShowReactivate] = useState(false)
  const [currentCustomer, setCurrentCustomer] = useState(customer)
  const [refreshKey, setRefreshKey] = useState(0)
  const navigate = useNavigate()
  let mounted = true
  useEffect(() => {
    loadCustomer()

    return () => {
      mounted = false
    }
  }, [])

  async function loadCustomer() {
    try {
      const response = await merchantApi.customers.show(customer.id)
      if (!mounted) return
      const detail = { ...normalizeCustomer(unwrapItem(response)), id: customer.id }
      setCurrentCustomer(detail)
    } catch {
      setCurrentCustomer(customer)
    }
  }

  const refreshCustomer = async () => {
    try {
      const response = await merchantApi.customers.show(currentCustomer.id)
      const detail = { ...normalizeCustomer(unwrapItem(response)), id: currentCustomer.id }
      setCurrentCustomer(detail)
      setRefreshKey((value) => value + 1)
      onCustomerUpdated?.(detail)
    } catch {
      setRefreshKey((value) => value + 1)
    }
  }

  const handleSuspend = async (reason) => {
    try {
      await merchantApi.customers.update(currentCustomer.id, {
        status: -1,
        reason: reason,
      })
      await refreshCustomer()
    } catch (err) {
      // Keep UI responsive; API errors will be visible in browser console for now.
      console.error(err)
    }
  }

  const handleDeactivate = async (reason) => {
    try {
      await merchantApi.customers.update(currentCustomer.id, {
        status: 0,
        reason: reason,
      })
      await refreshCustomer()
    } catch (err) {
      // Keep UI responsive; API errors will be visible in browser console for now.
      console.error(err)
    }
  }

  const handleRestore = async (reason) => {
    try {
      await merchantApi.customers.update(currentCustomer.id, {
        status: 1
      })
      await refreshCustomer()
    } catch (err) {
      // Keep UI responsive; API errors will be visible in browser console for now.
      console.error(err)
    }
  }

  return (
    <>
      <div className="customers-details-overlay" onClick={onClose} />

      <aside className="customers-details-panel">
        <div className="customers-details-panel__header">
          <h3>Customer details</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="cdp-hero">
          {
            currentCustomer.avatar ?
              <img src={currentCustomer.avatar} alt={currentCustomer.name} className="cdp-hero__avatar" />
              : <Avatar className="sr-table__mui-avatar" />
          }
          <div className="cdp-hero__info">
            <div className="cdp-hero__name-row">
              <h4 className="cdp-hero__name">{currentCustomer.name}</h4>
              <span className={`customers-status customers-status--${currentCustomer.status}`}><Icon icon="ph:circle-fill" height={5} /> {currentCustomer.status.charAt(0).toUpperCase() + currentCustomer.status.slice(1)}</span>
            </div>
            <p className="cdp-hero__meta">
              <Icon icon="ph:star-fill" height={13} className="cdp-hero__star" />
              {currentCustomer.points.toLocaleString()} pts
              <span className="cdp-hero__sep">•</span>
              {currentCustomer.phone}
              <span className="cdp-hero__sep">•</span>
              Member since {currentCustomer.memberSince}
            </p>
          </div>
        </div>

        {
          currentCustomer.status === 'suspended' &&
          <div className='cdp-notification-container'>
            <Icon icon={'ph:info'} className='cdp-notification-container--icon-style' height={20} />
            <div>
              <div className="cdp-notification-container--header">Account suspended</div>
              <div className="cdp-notification-container--display-row">
                <div >Reason :</div>
                <div className="cdp-notification-container--header">{currentCustomer.reason || '-'}</div>
              </div>
              <div className="cdp-notification-container--display-row">
                <div>Suspended by :</div>
                <div className="cdp-notification-container--header">{currentCustomer.actionBy || '-'}</div>
              </div>
            </div>
          </div>
        }
        {
          currentCustomer.status === 'inactive' &&
          <div className='cdp-notification-container cdp-notification-container--inactive'>
            <Icon icon={'ph:moon'} className='cdp-notification-container--icon-style' height={20} />
            <div>
              <div className="cdp-notification-container--header">Account inactive</div>
              <div className="cdp-notification-container--display-row">
                <div >Reason :</div>
                <div className="cdp-notification-container--header">{currentCustomer.reason || '-'}</div>
              </div>
              <div className="cdp-notification-container--display-row">
                <div>Suspended by :</div>
                <div className="cdp-notification-container--header">{currentCustomer.actionBy || '-'}</div>
              </div>
            </div>
          </div>
        }

        <div className="cdp-tabs">
          {['overview', 'orders', 'points'].map((tab) => (
            <button
              key={tab}
              className={`cdp-tab ${activeTab === tab ? 'cdp-tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="customers-details-panel__body">
          {activeTab === 'overview' && (
            <OverviewTab
              customer={currentCustomer}
              onAdjustPoints={() => setShowAdjust(true)}
              onSuspend={() => setShowSuspend(true)}
              onDeactivate={() => setShowDeactivate(true)}
              onReactivate={() => setShowReactivate(true)}
              onRestore={() => setShowRestore(true)}
              editable={editable}
            />
          )}
          {activeTab === 'orders' && <OrdersTab customer={currentCustomer} navigate={navigate} />}
          {activeTab === 'points' && (
            <PointsTab customer={currentCustomer} onAdjustPoints={() => setShowAdjust(true)} refreshKey={refreshKey} />
          )}
        </div>
      </aside>

      {showAdjust && (
        <AdjustPointsModal customer={currentCustomer} onClose={() => setShowAdjust(false)} onAdjusted={refreshCustomer} />
      )}
      {showSuspend && (
        <SuspendModal customer={currentCustomer} onClose={() => setShowSuspend(false)} onConfirm={handleSuspend} />
      )}
      {showDeactivate && (
        <DeactivateModal customer={currentCustomer} onClose={() => setShowDeactivate(false)} onConfirm={handleDeactivate} />
      )}
      {showRestore && (
        <RestoreModal customer={currentCustomer} onClose={() => setShowRestore(false)} onConfirm={handleRestore} />
      )}
      {showReactivate && (
        <ReactivateModal customer={currentCustomer} onClose={() => setShowReactivate(false)} onConfirm={handleRestore} />
      )}
    </>
  )
}

function CustomerFilter({ open, setOpen, setValues, values, applyFilters }) {
  const filters = [
    {
      key: 'pointBalance',
      label: 'Point Balance',
      type: 'number-range',
      fromPlaceholder: '0',
      toPlaceholder: '100,000',
    },
    {
      key: 'totalSpent',
      label: 'Total Spent',
      type: 'number-range',
      fromLabel: 'From (RM)',
      toLabel: 'To (RM)',
    },
    {
      key: 'createdAt',
      label: 'Created',
      type: 'date-range',
    },
  ]

  return (
    <FilterSideModal
      open={open}
      onClose={() => setOpen(false)}
      onApply={(vals) => {
        setOpen(false)
        applyFilters()
      }}
      onReset={() => setValues({})}
      values={values}
      onChange={(key, val) => setValues((p) => ({ ...p, [key]: val }))}
      filters={filters}
    />
  )
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [activeStatus, setActiveStatus] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [userInfo, setUserInfo] = useState(null)
  const [currentPage, setCurrentPage] = useState(null)
  const [meta, setMeta] = useState(null)
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterValues, setFilterValues] = useState({})
  const [sourceList, setSourceList] = useState(null)
  //permission states
  const [editable, setEditable] = useState(true)
  const [exportable, setExportable] = useState(true)

  const navigate = useNavigate()

  const searchTimeoutRef = useRef(null)

  const columns = [
    {
      field: 'name',
      header: 'Name',
      render: (row) => (
        <div className="sr-table__member">
          {
            row.profile_photo_url ?
              <img src={row.profile_photo_url} alt={row.name} className="sr-table__avatar" />
              : <Avatar className="sr-table__mui-avatar" />
          }

          <span>{row.name}</span>
        </div>
      ),
    },
    {
      field: 'phone',
      header: 'Phone',
    },
    {
      field: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`customers-status customers-status--${row.status}`}>
          <Icon icon="ph:circle-fill" height={5} /> {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : ''}
        </span>
      ),
    },
    {
      field: 'points',
      header: 'Points',
      render: (row) => (
        <span>
          {row.points.toLocaleString()} pts
        </span>
      ),
    },
    {
      field: 'orders',
      header: 'Orders',
    },
    {
      field: 'totalSpent',
      header: 'Total Spent',
    },
    {
      field: 'lastLogin',
      header: 'Last Login',
    },

    {
      field: 'actions',
      header: '',
      render: (row) => (<Icon icon={'ph:caret-right'} height={16} />)
    }
  ]

  const loadCustomers = async (searchValue = searchTerm) => {
    setLoading(true)
    setError('')
    console.log("filterValues", filterValues)
    try {
      const response = await merchantApi.customers.list({
        page: currentPage || 1,
        "filter[status]": activeStatus === 'all' ? undefined : activeStatus === 'active' ? 1 : activeStatus === 'suspended' ? -1 : 0,
        "filter[point_balance_from]": filterValues?.pointBalance?.from || undefined,
        "filter[point_balance_to]": filterValues?.pointBalance?.to || undefined,
        "filter[total_spent_from]": filterValues?.totalSpent?.from || undefined,
        "filter[total_spent_to]": filterValues?.totalSpent?.to || undefined,
        "filter[created_from]": filterValues?.createdAt?.start || undefined,
        "filter[created_to]": filterValues?.createdAt?.end || undefined,
        "search": searchValue || undefined,
      })
      setCustomers(unwrapList(response).map(normalizeCustomer))
      setMeta({
        from: response?.data?.from || null,
        to: response?.data?.to || null,
        total: response?.data?.total || null,
        perPage: response?.data?.per_page || null,
        currentPage: response?.data?.current_page || null,
        counts: response?.meta?.tabs?.map((tab) => ({ id: tab.id || tab.key, label: tab.name, value: tab.key, count: tab.customer_count || 0, visibility: tab?.filter ? tab.filter.visibility : 1 })) || []
      } || null)
    } catch (err) {
      setError(err?.message || 'Failed to load customers.')
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {

    try {
      const response = await merchantApi.auth.me()
      if (!response?.data?.permissions?.includes(`view-${moduleCode}`)) {
        navigate('/unauthorized')
        return
      }
      const generalData = await merchantApi.general.systemInfo()
      setUserInfo(response?.data || null);
      setSourceList(generalData?.data?.sources || null)
      // setEditable(response?.data?.permissions?.includes(`edit-${moduleCode}`))
      setExportable(response?.data?.permissions?.includes(`export-${moduleCode}`))
    } catch (err) {
      setError(err?.message || 'Failed to load user info.')
      setUserInfo(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  useEffect(() => {
    loadCustomers()
  }, [currentPage, activeStatus])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const counts = useMemo(() => {
    console.log("check meta counts", meta?.counts)
    const active = meta?.counts?.find((tab) => tab.value === 'active')?.count || 0
    const suspended = meta?.counts?.find((tab) => tab.value === 'suspended')?.count || 0
    const inactive = meta?.counts?.find((tab) => tab.value === 'inactive')?.count || 0

    return {
      all: customers.length,
      active,
      suspended,
      inactive,
    }
  }, [meta])

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      return customer?.id
    })
  }, [customers])

  const updateCustomerInList = (updatedCustomer) => {
    setCustomers((items) => items.map((item) => (item.id === updatedCustomer.id ? updatedCustomer : item)))
    setSelectedCustomer(updatedCustomer)
  }

  const handleExport = async () => {
    setLoading(true)
    setError('')

    let payload = {
      "filter[status]": activeStatus === 'all' ? undefined : activeStatus === 'active' ? 1 : activeStatus === 'suspended' ? -1 : 0,
      "filter[search]": searchTerm || undefined,
      "filter[point_balance_from]": filterValues?.pointBalance?.from || undefined,
      "filter[point_balance_to]": filterValues?.pointBalance?.to || undefined,
      "filter[total_spent_from]": filterValues?.totalSpent?.from || undefined,
      "filter[total_spent_to]": filterValues?.totalSpent?.to || undefined,
      "filter[created_from]": filterValues?.createdAt?.from || undefined,
      "filter[created_to]": filterValues?.createdAt?.to || undefined,
    }

    try {
      const response = await merchantApi.customers.export(payload)
      // download the file from the response base64 data
      const byteCharacters = atob(response?.data?.base64);
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: response?.data?.mime_type || 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = response?.data?.file_name || 'customers.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (err) {
      setError(err?.message || 'Failed to export customers.')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (text) => {
    setSearchTerm(text)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1)
      loadCustomers(text)
    }, 800)
  }

  const onPageChange = (page) => {
    setCurrentPage(page)
  }

  return (
    <div className="customers-layout">
      <main className="customers-page">
        <Header
          items={[
            { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
            { label: 'Customer', current: true },
          ]}
        />
        <div className="customers-page__header">
          <div>

            <h1>Customers</h1>
            {/* <p>{userInfo?.default_outlet?.name || 'Outlet'} - {meta?.total || '0'} registered customer</p> */}
            {error && <p style={{ color: '#b42318' }}>{error}</p>}
          </div>
          {exportable && (
            <button className="customers-page__export-btn" onClick={() => handleExport()}>
              <Download size={18} /> Export CSV
            </button>
          )}
        </div>

        <div className="customers-page__tabs">
          <button className={activeStatus === 'all' ? 'active' : ''} onClick={() => setActiveStatus('all')}>All customers ({counts.all})</button>
          <button className={activeStatus === 'active' ? 'active' : ''} onClick={() => setActiveStatus('active')}>Active ({counts.active})</button>
          <button className={activeStatus === 'suspended' ? 'active' : ''} onClick={() => setActiveStatus('suspended')}>Suspended ({counts.suspended})</button>
          <button className={activeStatus === 'inactive' ? 'active' : ''} onClick={() => setActiveStatus('inactive')}>Inactive ({counts.inactive})</button>
        </div>

        <div className="customers-page__filters">
          <div className="customers-page__filters-left">
            <button className="customers-page__filter-btn" onClick={() => setFilterOpen(true)}>
              <Icon icon={'ph:funnel-simple'} className='customers-page__filter-icon' />Filters
            </button>
          </div>
          <div className="customers-page__search">
            <Search size={18} />
            <input placeholder="Search users" value={searchTerm} onChange={(event) => handleSearch(event.target.value)} />
          </div>
        </div>

        <AppTable
          columns={columns}
          rows={filteredCustomers || []}
          pagination={meta}
          loading={loading}
          onRowClick={(row) => { setSelectedCustomer(row) }}
          onPageChange={(page) => {
            onPageChange(page)
          }}
        />
      </main>

      {selectedCustomer && (
        <CustomerDetailsPanel
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onCustomerUpdated={updateCustomerInList}
          editable={editable}
        />
      )}

      <CustomerFilter
        open={filterOpen}
        setOpen={setFilterOpen}
        values={filterValues}
        setValues={setFilterValues}
        applyFilters={loadCustomers}
      />
    </div>
  )
}
